<template>
  <div class="login-wrapper">
    <!-- Left Section: Precision Form Area (flex: 1.25) -->
    <div class="login-left">
      <div class="login-brand-lockup" aria-label="鲸夕智绘">
        <BrandLogo :size="64" variant="full" />
      </div>

      <div class="form-container">
        <!-- Welcome Title & Subtitle -->
        <h2 class="welcome-title">欢迎登录</h2>
        <p class="welcome-subtitle">请使用企业微信扫一扫安全接入指挥中心</p>

        <!-- QR Code Dedicated Container -->
        <div class="qr-content-wrapper">
          <div class="qr-mode-section">
            <!-- WeCom QR Code Card -->
            <div class="wecom-qr-card">
              <div class="qr-box-wrapper">
                <div class="qr-top-header">
                  <svg class="wecom-mini-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                    <path fill="#07C160" d="M12 2C6.48 2 2 6.03 2 11c0 2.87 1.51 5.42 3.86 7.08L5 21l3.29-1.09C9.57 20.26 10.76 20.5 12 20.5c5.52 0 10-4.03 10-9.5S17.52 2 12 2z"/>
                  </svg>
                  <span class="qr-header-text">企业微信扫码登录</span>
                </div>

                <div class="qr-bracket-frame">
                  <div class="bracket top-left"></div>
                  <div class="bracket top-right"></div>
                  <div class="bracket bottom-left"></div>
                  <div class="bracket bottom-right"></div>

                  <div class="wx-qr-viewport" :class="{ 'is-live': isQrRendered && !wecomAuthPending }">
                    <div v-if="wecomAuthPending" class="qr-callback-panel">
                      <span class="qr-loading-spinner" aria-hidden="true"></span>
                      <span>{{ isLoading ? '正在验证企微登录…' : '登录未完成，正在刷新二维码…' }}</span>
                    </div>

                    <template v-else>
                      <div id="wx_login_container" class="wx-qr-code-embed"></div>

                      <div v-if="!isQrRendered && !isQrLoading && !qrError" class="qr-placeholder" aria-hidden="true">
                        <svg class="login-qr-svg" viewBox="8 8 184 184">
                          <rect x="8" y="8" width="184" height="184" fill="#ffffff"/>
                          <g fill="#18181b">
                            <rect x="12" y="12" width="48" height="48" rx="10"/>
                            <rect x="19" y="19" width="34" height="34" fill="#ffffff" rx="6"/>
                            <rect x="26" y="26" width="20" height="20" fill="#18181b" rx="3"/>
                            <rect x="140" y="12" width="48" height="48" rx="10"/>
                            <rect x="147" y="19" width="34" height="34" fill="#ffffff" rx="6"/>
                            <rect x="154" y="26" width="20" height="20" fill="#18181b" rx="3"/>
                            <rect x="12" y="140" width="48" height="48" rx="10"/>
                            <rect x="19" y="147" width="34" height="34" fill="#ffffff" rx="6"/>
                            <rect x="26" y="154" width="20" height="20" fill="#18181b" rx="3"/>
                          </g>
                        </svg>
                      </div>
                    </template>
                  </div>
                </div>

                <div v-if="isQrLoading && !wecomAuthPending" class="qr-loading-overlay">
                  <span class="qr-loading-spinner" aria-hidden="true"></span>
                  <span>正在生成二维码…</span>
                </div>

                <div v-else-if="qrError && !isQrRendered && !wecomAuthPending" class="qr-error-panel">
                  <p class="qr-error-text">{{ qrError }}</p>
                  <button type="button" class="qr-retry-btn" @click="resetWeComLoginSurface">重新扫码</button>
                </div>
              </div>
            </div>

            <!-- Bottom Tip Pill -->
            <div class="wecom-tip-pill">
              <span class="tip-icon">💬</span>
              <span>{{ wecomTipText }}</span>
            </div>
            <p v-if="displayAuthError" class="auth-error">
              {{ displayAuthError }}
              <button
                v-if="wecomAuthFailed"
                type="button"
                class="auth-retry-link"
                @click="resetWeComLoginSurface"
              >重新扫码</button>
            </p>
          </div>
        </div>

        <!-- Server Online Badge (Centered at bottom) -->
        <div class="server-badge-wrapper">
          <div class="server-badge">
            <span class="status-dot" :class="{ busy: isLoading }">●</span>
            <span>{{ isLoading ? 'AUTHENTICATING…' : 'SERVER: ONLINE' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Presentation Area: High-End Jingxi AI Vector Brand Badge -->
    <div class="login-right">
      <!-- Slogan Section (Exact Slogan Lines Requested by User) -->
      <div class="slogan-box">
        <div class="handwriting-sub">Your eyes, your rules.</div>
        <div class="slogan-lines">
          <div class="slogan-line">驱动店铺</div>
          <div class="slogan-line highlight">数字化</div>
          <div class="slogan-line">提高设计</div>
          <div class="slogan-line highlight">能效</div>
        </div>
      </div>

      <!-- Mascot Showcase Container with Asymmetrical Organic Blob Outline -->
      <div
        ref="mascotRoot"
        class="mascot-circle-container"
        :class="{ 'is-dizzy': isDizzy }"
        :style="blobCardTiltStyle"
      >
        <!-- Asymmetrical Double Outer Line matching Reference Screenshot -->
        <div class="organic-outline-ring"></div>

        <!-- Organic Fluid Blob Frame displaying Full Rich Studio Environment -->
        <div class="mascot-blob-frame">
          <img 
            :src="cdpUrl('assets/claymation-van-gogh.jpg') + '?v=studio_glasses_exact_v6'" 
            alt="鲸夕智绘 眼睛精准叠合黑框眼镜镜片" 
            class="mascot-img" 
          />

          <!-- Interactive Eye Overlay Elements (Layered 1:1 EXACTLY over Glasses Lenses in Screenshot) -->
          <div class="interactive-eyes-overlay">
            <!-- Left Eye Socket (Viewer's Left Lens) -->
            <div ref="eyeLeft" class="eye-socket left-eye-socket">
              <div 
                class="eye-pupil" 
                :style="{ transform: `translate(${leftPupilPos.x}px, ${leftPupilPos.y}px)` }"
              >
                <div class="pupil-glint"></div>
              </div>
            </div>

            <!-- Right Eye Socket (Viewer's Right Lens) -->
            <div ref="eyeRight" class="eye-socket right-eye-socket">
              <div 
                class="eye-pupil" 
                :style="{ transform: `translate(${rightPupilPos.x}px, ${rightPupilPos.y}px)` }"
              >
                <div class="pupil-glint"></div>
              </div>
            </div>
          </div>
          <div class="dizzy-face" aria-hidden="true">
            <span class="dizzy-mouth"></span>
          </div>
        </div>

        <!-- Top Right Dynamic Glowing Sparkles / Stars (Dynamic Twinkle & Pulse) -->
        <div class="sparkles-container">
          <div class="sparkle sparkle-1">✦</div>
          <div class="sparkle sparkle-2">✦</div>
        </div>
        <div class="dizzy-stars" aria-hidden="true">
          <span>✦</span>
          <span>★</span>
          <span>✦</span>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, nextTick } from 'vue';
import BrandLogo from '@/components/BrandLogo.vue';
import { cdpUrl } from '@/utils/platformHelpers';
import { useMascotPointerInteraction } from '@/composables/useMascotPointerInteraction';
import { loginByWeComCode } from '@/services/authStore';
import {
  useWeComQrLogin,
  resolveWeComConfig,
  clearWeComOAuthQuery,
} from '@/composables/useWeComQrLogin';

const emit = defineEmits(['login-success']);

const isLoading = ref(false);
const authError = ref('');
const wecomAuthPending = ref(false);
const wecomAuthFailed = ref(false);

const {
  isQrLoading,
  isQrRendered,
  qrError,
  initQrLogin,
  reinitQrLogin,
} = useWeComQrLogin({
  enabled: computed(() => !wecomAuthPending.value),
});

const displayAuthError = computed(() => authError.value || qrError.value);

const wecomTipText = computed(() => {
  if (isLoading.value) return '正在用企业微信授权码验证登录…';
  if (isQrLoading.value) return '正在生成企业微信登录二维码…';
  if (resolveWeComConfig()) {
    return '请使用企业微信扫一扫安全登录';
  }
  return '未检测到企微配置，请检查环境变量 VITE_WECOM_CORP_ID 与 VITE_WECOM_AGENT_ID';
});

const {
  mascotRoot,
  eyeLeft,
  eyeRight,
  leftPupilPos,
  rightPupilPos,
  blobCardTiltStyle,
  isDizzy,
} = useMascotPointerInteraction();

onMounted(async () => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  const savedState = localStorage.getItem('wecom_state');
  if (code) {
    wecomAuthPending.value = true;
    wecomAuthFailed.value = false;

    const parseNonce = (s) => (s ? String(s).split('|')[0] : '');
    const stateNonce = parseNonce(state);
    const savedNonce = parseNonce(savedState);

    if (stateNonce && savedNonce && stateNonce !== savedNonce) {
      authError.value = '企业微信登录状态校验失败，请重新扫码';
      wecomAuthFailed.value = true;
      await resetWeComLoginSurface();
      return;
    }
    await handleLogin({ code });
  }
});

const clearAuthQuery = () => {
  clearWeComOAuthQuery();
};

/** 登录失败或 state 异常：清 URL 并强制重新生成可扫二维码 */
async function resetWeComLoginSurface() {
  clearAuthQuery();
  localStorage.removeItem('wecom_state');
  wecomAuthPending.value = true;
  await nextTick();
  wecomAuthPending.value = false;
  await reinitQrLogin();
}

async function handleLogin(options = {}) {
  isLoading.value = true;
  authError.value = '';
  wecomAuthFailed.value = false;
  try {
    const code = options?.code || new URLSearchParams(window.location.search).get('code');
    if (!code) {
      throw new Error('未获取到企业微信授权码，请重新扫码');
    }

    wecomAuthPending.value = true;
    const auth = await loginByWeComCode(code);
    clearAuthQuery();
    localStorage.removeItem('wecom_state');
    wecomAuthPending.value = false;

    emit('login-success', {
      role: auth.loginRole,
      auth,
      loginType: 'wecom',
    });
  } catch (error) {
    const message = error?.message || '企业微信登录失败，请稍后重试';
    authError.value = message;
    if (import.meta.env.DEV) console.warn('[Login] 企微认证失败', error);

    wecomAuthFailed.value = true;
    authError.value = `${message}，请重新扫码`;
    await resetWeComLoginSurface();
  } finally {
    isLoading.value = false;
  }
}
</script>


<style scoped>
.login-wrapper {
  display: flex;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'PingFang SC', sans-serif;
  background: #ffffff;
}

/* Left Form Area (Wider Flex Ratio 1.25) */
.login-left {
  flex: 1.25;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 40px 60px;
}

.login-brand-lockup {
  position: absolute;
  top: 40px;
  left: 60px;
  z-index: 5;
}

.form-container {
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
}

/* Welcome Title & Subtitle */
.welcome-title {
  font-size: 26px;
  font-weight: 800;
  color: #1f1f1f;
  text-align: center;
  margin: 0 0 6px 0;
  letter-spacing: -0.02em;
}

.welcome-subtitle {
  font-size: 13px;
  color: #888888;
  text-align: center;
  margin: 0 0 20px 0;
}

/* Tab Switcher Capsule Bar */
.tab-switcher {
  display: flex;
  background: #f5f6f8;
  border-radius: 20px;
  padding: 4px;
  margin-bottom: 20px;
}

.tab-btn {
  flex: 1;
  height: 38px;
  border-radius: 16px;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  color: #666666;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-btn.active {
  background: #ffffff;
  color: #7c3aed;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* 二维码内容区：为企微 iframe 保留完整可视空间 */
.tab-content-wrapper {
  height: 500px;
  margin-bottom: 20px;
  box-sizing: border-box;
}

.qr-mode-section,
.auth-mode-section {
  height: 500px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

/* TAB 1: WeCom QR Code Card */
.wecom-qr-card {
  height: auto;
  min-height: 424px;
  background: transparent;
  border-radius: 24px;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  box-sizing: border-box;
  border: none;
  box-shadow: none;
}

.qr-box-wrapper {
  width: 388px;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 20px;
  padding: 20px 20px 24px;
  box-sizing: border-box;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.qr-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.92);
  font-size: 12px;
  color: #666666;
  z-index: 4;
  border-radius: 18px;
}

.qr-error-panel {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.96);
  z-index: 4;
  border-radius: 18px;
  box-sizing: border-box;
}

.qr-error-text {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: #cf1322;
  text-align: center;
}

.qr-retry-btn {
  border: 1px solid #7c3aed;
  background: #ffffff;
  color: #7c3aed;
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.qr-retry-btn:hover {
  background: #f5f3ff;
}

.qr-loading-spinner {
  width: 22px;
  height: 22px;
  border: 2px solid #e6e6e6;
  border-top-color: #7c3aed;
  border-radius: 50%;
  animation: qrSpin 0.8s linear infinite;
}

@keyframes qrSpin {
  to { transform: rotate(360deg); }
}

.qr-top-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
}

.qr-header-text {
  font-size: 16px;
  font-weight: 700;
  color: #7c3aed;
}

.qr-bracket-frame {
  position: relative;
  width: 344px;
  height: 344px;
  box-sizing: border-box;
}

.wx-qr-viewport {
  position: absolute;
  inset: 20px;
  overflow: visible;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  z-index: 1;
}

.wx-qr-code-embed {
  width: 100%;
  height: 100%;
  position: relative;
}

.wx-qr-viewport.is-live .wx-qr-code-embed :deep(iframe) {
  width: 272px !important;
  height: 272px !important;
  border: none !important;
  position: absolute;
  top: 50%;
  left: 50%;
  margin: 0;
  transform: translate(-50%, -50%);
  pointer-events: auto;
}

.qr-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-qr-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.bracket {
  position: absolute;
  width: 16px;
  height: 16px;
  border-color: #7c3aed;
  border-style: solid;
  pointer-events: none;
  z-index: 3;
  opacity: 0.95;
}

.bracket.top-left {
  top: 0;
  left: 0;
  border-width: 3px 0 0 3px;
  border-top-left-radius: 5px;
}

.bracket.top-right {
  top: 0;
  right: 0;
  border-width: 3px 3px 0 0;
  border-top-right-radius: 5px;
}

.bracket.bottom-left {
  bottom: 0;
  left: 0;
  border-width: 0 0 3px 3px;
  border-bottom-left-radius: 5px;
}

.bracket.bottom-right {
  bottom: 0;
  right: 0;
  border-width: 0 3px 3px 0;
  border-bottom-right-radius: 5px;
}

.auth-error {
  margin: 10px 0 0;
  text-align: center;
  font-size: 12px;
  color: #cf1322;
  line-height: 1.4;
}

.auth-retry-link {
  margin-left: 8px;
  border: none;
  background: none;
  color: #7c3aed;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.auth-retry-link:hover {
  text-decoration: underline;
}

.qr-callback-panel {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: #ffffff;
  font-size: 13px;
  color: #666666;
  z-index: 2;
}

.quick-role-item:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.status-dot.busy {
  color: #faad14;
}

/* Tip Pill */
.wecom-tip-pill {
  width: 100%;
  height: 44px;
  background: #f5f6f8;
  border-radius: 16px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  color: #666666;
  font-weight: 500;
  box-sizing: border-box;
}

.tip-icon {
  font-size: 14px;
}

/* TAB 2: Quick Roles Mode Card */
.quick-roles-card {
  height: 316px;
  background: #f5f6f8;
  border-radius: 24px;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
  border: 1px solid #eeeeee;
}

.quick-roles-title {
  font-size: 13px;
  font-weight: 600;
  color: #888888;
  margin-bottom: 12px;
  text-align: center;
}

.quick-roles-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  flex: 1;
}

.quick-role-item {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  grid-template-rows: auto auto;
  align-items: center;
  align-content: center;
  column-gap: 10px;
  row-gap: 6px;
  background: #ffffff;
  padding: 14px 12px;
  border-radius: 14px;
  border: 1px solid #eeeeee;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
}

.role-info {
  display: contents;
}

.quick-role-item:hover {
  border-color: #7c3aed;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(124, 58, 237, 0.15);
}

.role-avatar-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f5f3ff;
  color: #7c3aed;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

.role-name {
  grid-column: 2;
  grid-row: 1;
  font-size: 13px;
  font-weight: 700;
  color: #1f1f1f;
}

.role-desc {
  grid-column: 1 / -1;
  grid-row: 2;
  font-size: 10px;
  color: #888888;
  line-height: 1.4;
  white-space: nowrap;
}

/* Server Badge Wrapper */
.server-badge-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
}

.server-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f5f6f8;
  padding: 6px 16px;
  border-radius: 14px;
  font-size: 11px;
  font-weight: 600;
  color: #888888;
  font-family: monospace;
}

.status-dot {
  color: #7c3aed;
  font-size: 10px;
}

/* Right Presentation Area (Asymmetrical Organic Oval Blob Frame from Reference Screenshot) */
.login-right {
  flex: 0.9;
  background: #edf5bf;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 40px 30px;
}

.slogan-box {
  position: absolute;
  top: 8%;
  left: 8%;
  z-index: 3;
}

.handwriting-sub {
  font-family: 'Caveat', 'Ma Shan Zheng', cursive;
  font-size: 28px;
  font-weight: 700;
  color: #829a21;
  line-height: 1.1;
  margin-bottom: 8px;
  transform: rotate(-2deg);
}

.slogan-lines {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.slogan-line {
  font-size: 34px;
  font-weight: 900;
  color: #1a1a1a;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.slogan-line.highlight {
  color: #386111;
}

/* Mascot Container: Interactive 3D Parallax Tilt */
.mascot-circle-container {
  position: relative;
  width: 390px;
  height: 390px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 30px;
  margin-top: 40px;
  will-change: transform;
}

.mascot-circle-container.is-dizzy {
  /* static clean */
}

/* Asymmetrical Double Outer Line matching Reference Screenshot */
.organic-outline-ring {
  position: absolute;
  top: -12px;
  left: -12px;
  width: 394px;
  height: 394px;
  border-radius: 54% 46% 42% 58% / 58% 44% 56% 42%;
  border: 2px solid #829a21;
  pointer-events: none;
  z-index: 4;
  transform: rotate(-3deg);
}

/* Asymmetrical Organic Fluid Blob Frame displaying Full Rich Studio Environment */
.mascot-blob-frame {
  width: 370px;
  height: 370px;
  border-radius: 54% 46% 42% 58% / 58% 44% 56% 42%;
  background: radial-gradient(circle, #e6f663 0%, #cce034 100%);
  position: relative;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(85, 110, 15, 0.22);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  transform: rotate(-3deg);
}

.mascot-img {
  width: 370px;
  height: 370px;
  object-fit: cover;
  object-position: center top;
  transform: rotate(3deg);
}

.is-dizzy .mascot-img {
  /* static clean */
}

/* Interactive Eye Overlay Elements (Layered 1:1 EXACTLY over Designer's Glasses Lenses) */
.interactive-eyes-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 5;
  transform: rotate(3deg);
}

.eye-socket {
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  border: 2px solid #18181b;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
}

.left-eye-socket {
  top: 26.8%;
  left: 55.8%;
}

.right-eye-socket {
  top: 26.8%;
  left: 63.8%;
}

.eye-pupil {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #18181b;
  position: relative;
  transition: transform 0.05s ease-out;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 1px;
}

.pupil-glint {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #ffffff;
}

.is-dizzy .eye-pupil {
  width: 12px;
  height: 12px;
  padding: 0;
  background: conic-gradient(#18181b, #ffffff, #18181b, #ffffff, #18181b);
  transition: none;
  animation: dizzyPupilSpin 0.32s linear infinite;
}

.is-dizzy .right-eye-socket .eye-pupil {
  animation-direction: reverse;
}

.is-dizzy .pupil-glint {
  display: none;
}

@keyframes dizzyPupilSpin {
  to { transform: rotate(360deg); }
}

.dizzy-face {
  position: absolute;
  inset: 0;
  z-index: 6;
  opacity: 0;
  pointer-events: none;
  transform: rotate(3deg);
  transition: opacity 0.15s ease;
}

.is-dizzy .dizzy-face {
  opacity: 1;
}

.dizzy-mouth {
  position: absolute;
  top: 38.8%;
  left: 60.5%;
  width: 23px;
  height: 7px;
  border-top: 3px solid #251712;
  border-radius: 50%;
  transform: rotate(5deg);
  filter: drop-shadow(0 1px 0 rgba(255, 255, 255, 0.45));
}

.dizzy-stars {
  position: absolute;
  top: 60px;
  right: 70px;
  width: 120px;
  height: 78px;
  z-index: 9;
  opacity: 0;
  pointer-events: none;
}

.is-dizzy .dizzy-stars {
  opacity: 1;
  animation: dizzyStarOrbit 0.8s linear infinite;
}

.dizzy-stars span {
  position: absolute;
  color: #f1a21d;
  font-size: 22px;
  filter: drop-shadow(0 0 7px rgba(241, 162, 29, 0.75));
}

.dizzy-stars span:nth-child(1) { top: 3px; left: 12px; }
.dizzy-stars span:nth-child(2) { top: 22px; right: 2px; color: #55a8ff; font-size: 17px; }
.dizzy-stars span:nth-child(3) { bottom: 0; left: 48px; color: #ff6868; font-size: 15px; }

@keyframes dizzyStarOrbit {
  from { transform: rotate(0deg) scale(0.96); }
  50% { transform: rotate(180deg) scale(1.08); }
  to { transform: rotate(360deg) scale(0.96); }
}

/* Top Right Dynamic Sparkles (Rotating, Twinkling & Breathing Glow) */
.sparkles-container {
  position: absolute;
  top: 10px;
  right: 15px;
  z-index: 5;
}

.sparkle {
  position: absolute;
  color: #e59a1f;
  font-weight: bold;
}

.sparkle-1 {
  top: 0;
  right: 0;
  font-size: 28px;
  animation: sparkleTwinkle 3s ease-in-out infinite;
}

.sparkle-2 {
  top: 24px;
  right: 26px;
  font-size: 18px;
  animation: sparkleTwinkle 3s ease-in-out 1.5s infinite;
}

@keyframes sparkleTwinkle {
  0%, 100% {
    transform: scale(1) rotate(0deg);
    opacity: 0.9;
    filter: drop-shadow(0 0 4px rgba(229, 154, 31, 0.4));
  }
  50% {
    transform: scale(1.3) rotate(25deg);
    opacity: 1;
    filter: drop-shadow(0 0 12px rgba(229, 154, 31, 0.9));
  }
}

@media (max-width: 960px) {
  .login-right {
    display: none;
  }

  .login-left {
    padding-inline: 32px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mascot-circle-container,
  .mascot-blob-frame,
  .mascot-img,
  .eye-pupil,
  .dizzy-stars {
    animation: none !important;
    transition: none !important;
  }
}

@media (max-width: 420px) {
  .login-left {
    padding-inline: 16px;
  }

  .form-container {
    max-width: 100%;
  }

  .qr-box-wrapper {
    width: min(340px, 100%);
    padding-inline: 14px;
  }

  .qr-bracket-frame {
    width: min(304px, calc(100vw - 64px));
    height: auto;
    aspect-ratio: 1;
  }

  .wx-qr-viewport {
    inset: 16px;
  }

  .tab-content-wrapper,
  .qr-mode-section,
  .auth-mode-section {
    height: 470px;
  }

  .wecom-qr-card {
    min-height: 394px;
  }
}

@media (max-width: 360px) {
  .wx-qr-viewport.is-live .wx-qr-code-embed :deep(iframe) {
    transform: translate(-50%, -50%) scale(0.88);
  }
}
</style>
